import 'twin.macro';
import React from 'react';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { Field, Form } from 'react-final-form';
import Link from '@teetsh/app/src/components/Link';
import InputComponent from '@teetsh/app/src/components/Form/InputGroup/formWrapper';
import { composeValidators } from '@teetsh/app/src/utils/formUtils';
import { FORM_ERROR } from 'final-form';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { useAuth } from '@teetsh/app/src/containers/App/context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  EnvelopeIcon as Mail,
  LockClosedIcon as LockClosed,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useTranslatedValidation } from 'utils/useTranslatedValidation';

interface SignInForm {
  email: string;
  password: string;
}

export function SignIn() {
  const { login, loginGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['auth', 'common']);
  const { required, isValidEmail } = useTranslatedValidation();

  return (
    <div>
      <Form<SignInForm>
        onSubmit={values =>
          login(values)
            .then(() => navigate(location.pathname + location.search))
            .catch(err => {
              if (err?.response?.status === 401) {
                return {
                  password: t('signin.incorrectPasswordOrEmailError'),
                };
              }
              return { [FORM_ERROR]: t('common:messages.genericError') };
            })
        }
      >
        {({ handleSubmit, submitting }) => (
          <form tw="mb-2" onSubmit={handleSubmit}>
            <h2 tw="mb-4 md:mb-8 display leading-none">
              {t('auth:signin.signInToYourAccount')}
            </h2>
            <div tw="mb-3">
              <Field
                name="email"
                label={t('auth:email')}
                autoFocus
                type="email"
                component={InputComponent}
                large
                validate={composeValidators(required, isValidEmail)}
                parse={v => v.trim()}
                icon={<Mail tw="h-5 w-5" />}
                id="login-form-email"
              />
            </div>
            <div tw="mb-6">
              <Field
                name="password"
                type="password"
                icon={<LockClosed tw="h-5 w-5" />}
                validate={required}
                large
                component={InputComponent}
                label={t('auth:signin.password')}
                id="login-form-password"
              />
            </div>
            <Button
              type="submit"
              loading={submitting}
              large
              fill
              intent="primary"
              id="login-form-submit"
            >
              {t('auth:signin.connectButtonLabel')}
            </Button>
          </form>
        )}
      </Form>
      <div tw="flex flex-col items-center">
        <div tw="text-right py-3">
          <Link to="/reset-password" tw="text-sm">
            {t('auth:signin.forgotPassword')}
          </Link>
        </div>
        <div tw="my-4 text-neutral-quiet text-sm leading-4">
          {t('common:or')}
        </div>
        <GoogleLogin
          onSuccess={(credentialResponse: CredentialResponse) => {
            loginGoogle({
              tokenId: credentialResponse.credential as string,
            }).then(() => navigate(location.pathname));
          }}
          locale={i18n.language}
          onError={() => {
            // Handle error if necessary
          }}
        />
      </div>
    </div>
  );
}
